import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { addNotificationMessage, logout } from '../slices/appStateSlice';
import { Employe } from '../../models/Employe';
import { AGENCE, EMPLOYE, EQUIPE, IMPORTER, MAGASIN, VEHICULE } from '../../constants/routes';
import { Equipe } from '../../models/Equipe';
import { Magasin } from '../../models/Magasin';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { Agence } from '../../models/Agence';
import { Vehicule } from '../../models/Vehicule';

const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers: Headers, { getState }): Headers => {
        return setHeaders(headers, (getState() as RootState).appState.token);
    }
})
const baseQueryInterceptor: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        if (api.type === 'mutation') {
            api.dispatch(addNotificationMessage({ text: `${(result.error?.data as any)?.detail} (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
            return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;

        } else {
            switch (result.error.status) {
                case 401:
                case "FETCH_ERROR":
                    api.dispatch(logout());
                    return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
                case 403:
                    api.dispatch(addNotificationMessage({ text: `Vous ne possédez pas les autorisations nécessaires pour modifier cette donnée. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                    return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
                case 404:
                    api.dispatch(addNotificationMessage({ text: `La donnée est inexistante. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                    return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
                case 423:
                    api.dispatch(addNotificationMessage({ text: `La donnée est présentement en modification par un autre utilisateur. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                    return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
                default:
                    api.dispatch(addNotificationMessage({ text: `Problème interne, veuillez communiquer avec le département TI. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                    return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
            }
        }
    }
    else if (api.type === 'mutation') {
        api.dispatch(addNotificationMessage({ text: `La mise à jour a été effectuée avec succès!`, style: 'success', key: Math.random(), timeout: 3000 }));
    }
    return result
}

export const agencesLivraisonsAPI = createApi({
    reducerPath: 'agencesLivraisonsAPI',
    tagTypes: ["equipes", "employes"],
    baseQuery: baseQueryInterceptor,
    endpoints: (builder) => ({
        getEmployes: builder.query<Employe[], { agence: string, mag_num: number, date: string }>({
            query: ({ agence, mag_num, date }) => `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EMPLOYE}/${date}`,
            providesTags: ["employes"]
        }),
        getEquipes: builder.query<Equipe[], { agence: string, mag_num: number, date: string }>({
            query: ({ agence, mag_num, date }) => `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EQUIPE}/${date}`,
            providesTags: ["equipes"]
        }),
        getMagasins: builder.query<Magasin[], string>({
            query: (agence) => `${AGENCE}/${agence}${MAGASIN}`
        }),
        getAgences: builder.query<Agence[], void>({
            query: () => AGENCE
        }),
        getVehicules: builder.query<Vehicule[], { agence: string, mag_num: number }>({
            query: ({ agence, mag_num }) => `${AGENCE}/${agence}${MAGASIN}/${mag_num}${VEHICULE}`
        }),
        addVehicule: builder.mutation<void, { agence: string, mag_num: number, date: string, body: Vehicule }>({
            query: ({ agence, mag_num, date, body }) => ({
                url: `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EQUIPE}/${date}${VEHICULE}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ["equipes"]
        }),
        addEquipesImporter: builder.mutation<void, { agence: string, mag_num: number, date: string, body: { dateReference: string } }>({
            query: ({ agence, mag_num, date, body }) => ({
                url: `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EQUIPE}/${date}${IMPORTER}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ["equipes", "employes"]
        }),
        updateEquipe: builder.mutation<Equipe, { agence: string, mag_num: number, date: string, vehicule: number, body: Equipe }>({
            query: ({ agence, mag_num, date, vehicule, body }) => ({
                url: `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EQUIPE}/${date}${VEHICULE}/${vehicule}`,
                method: 'PUT',
                body: body,
            }),
            invalidatesTags: ["equipes", "employes"]
        }),
        deleteVehicule: builder.mutation<void, { agence: string, mag_num: number, date: string, vehicule: number }>({
            query: ({ agence, mag_num, date, vehicule }) => ({
                url: `${AGENCE}/${agence}${MAGASIN}/${mag_num}${EQUIPE}/${date}${VEHICULE}/${vehicule}`,
                method: 'DELETE'
            }),
            invalidatesTags: ["equipes"]
        }),
    }),
})

function setHeaders(headers: Headers, token: string): Headers {
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', token);
    headers.set('x-applicat-apikey', process.env.REACT_APP_API_KEY!)
    return headers;
}

function getUrlParams(args: any) {
    let parameters = '';
    let operateur = '?';
    Object.keys(args).forEach(arg => {
        if ((args[arg] || args[arg] === 0) && args[arg] !== -1) {
            parameters = parameters.concat(`${operateur}${arg}=${args[arg]}`);
            operateur = operateur === '?' ? '&' : operateur;
        }
    });

    return parameters;
}

export const {
    useGetEmployesQuery,
    useGetEquipesQuery,
    useGetMagasinsQuery,
    useGetAgencesQuery,
    useUpdateEquipeMutation,
    useGetVehiculesQuery,
    useAddVehiculeMutation,
    useAddEquipesImporterMutation,
    useDeleteVehiculeMutation
} = agencesLivraisonsAPI;